import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { useGetCountries } from "api/queries/common/useGetCountries";
import { useGetSeaports } from "api/queries/common/useGetSeaports";
import { useGetShipmentSchedule } from "api/queries/shipments/useGetShipmentSchedule";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import FormField from "components/molecules/FormField/FormField";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { SeaportOptionType, mapSeaportsToOptions } from "helpers/editScheduleHelpers";
import useSearchQuery from "hooks/useSearchQuery";
import { IEditScheduleForm } from "../types";

export default function EditOceanForm() {
  const id = useParams().id?.toUpperCase()!;
  const intl = useIntl();
  const { setValue, watch } = useFormContext<IEditScheduleForm>();

  const { data: countriesData } = useGetCountries({
    refetchOnMount: false,
  });

  const { data: scheduleData = {} }: any = useGetShipmentSchedule(id, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const departurePortInitialOption = [
    {
      name: scheduleData.departure_port_name,
      un_locode: scheduleData.departure_port_code,
      city_name: scheduleData.departure_city,
      country_name: scheduleData.departure_country_name,
      country_code: scheduleData.departure_country_code,
      country_iso_code: scheduleData.departure_country_code,
    },
  ].map(mapSeaportsToOptions);

  const arrivalPortInitialOption = [
    {
      name: scheduleData.arrival_port_name,
      un_locode: scheduleData.arrival_port_code,
      city_name: scheduleData.arrival_city,
      country_name: scheduleData.arrival_country_name,
      country_code: scheduleData.arrival_country_code,
      country_iso_code: scheduleData.departure_country_code,
    },
  ].map(mapSeaportsToOptions);

  const { query: departurePortQuery, handleInputChange: handleDepartureInputChange } = useSearchQuery();
  const { query: arrivalPortQuery, handleInputChange: handleArrivalInputChange } = useSearchQuery();

  const { data: departurePortsData } = useGetSeaports(departurePortQuery);
  const { data: arrivalPortsData } = useGetSeaports(arrivalPortQuery);

  const departurePortOptions = departurePortQuery
    ? departurePortsData?.map(mapSeaportsToOptions) ?? []
    : departurePortInitialOption;
  // prettier-ignore
  const arrivalPortOptions = arrivalPortQuery
    ? arrivalPortsData?.map(mapSeaportsToOptions) ?? []
    : arrivalPortInitialOption;

  const [departureCity, arrivalCity] = watch(["departure_city_name", "arrival_city_name"]);
  const departureCountry = countriesData?.find((country) => country.value === watch("departure_country_iso_code"))?.label;
  const arrivalCountry = countriesData?.find((country) => country.value === watch("arrival_country_iso_code"))?.label;

  const handleSelectPort = (fieldName: "departure" | "arrival") => (port: SeaportOptionType[]) => {
    setValue(`${fieldName}_port_locode`, port[0]?.un_locode);
    setValue(`${fieldName}_country_iso_code`, port[0]?.country_iso_code);
    setValue(`${fieldName}_city_name`, port[0]?.city_name);
  };

  return (
    <>
      <FormField
        containerClasses="w-192"
        id="vessel_imo"
        field_id="vessel_imo"
        label={intl.formatMessage({
          id: "tracking.components.editScheduleDialog.vesselNumberIMO",
          defaultMessage: "Vessel number / IMO",
        })}
        eventContext="Edit Schedule Form"
        type="textinput"
      />

      <div className="d-flex">
        <div>
          <label>
            <FormattedMessage
              id="tracking.components.editScheduleDialog.vesselDepartureDate"
              defaultMessage="Vessel departure date"
            />
          </label>
          <DatePicker wrapperClassNames="w-192" name="departure_date" />
        </div>
      </div>

      <div className="d-flex">
        <SearchSelect
          classes="w-192"
          options={departurePortOptions}
          name="departure_port_name"
          label={intl.formatMessage({
            id: "tracking.components.editScheduleDialog.departurePort",
            defaultMessage: "Departure port",
          })}
          placeholder={intl.formatMessage({
            id: "general.search",
            defaultMessage: "Search",
          })}
          onInputChange={handleDepartureInputChange}
          onSelect={handleSelectPort("departure")}
          required
        />
        {(departureCity || departureCountry) && (
          <div className="ml-3">
            <label>
              {intl.formatMessage({
                id: "tracking.components.editScheduleDialog.departureCity",
                defaultMessage: "Departure city",
              })}
            </label>
            <div className="font-b4">
              {departureCity}
              {departureCity && departureCountry ? ", " : ""}
              {departureCountry}
            </div>
          </div>
        )}
      </div>

      <div className="d-flex">
        <div>
          <label>
            <FormattedMessage
              id="tracking.components.editScheduleDialog.vesselArrivalDate"
              defaultMessage="Vessel arrival date"
            />
          </label>
          <DatePicker wrapperClassNames="w-192" name="arrival_date" />
        </div>
      </div>

      <div className="d-flex">
        <SearchSelect
          classes="w-192"
          options={arrivalPortOptions}
          name="arrival_port_name"
          label={intl.formatMessage({
            id: "tracking.components.editScheduleDialog.arrivalPort",
            defaultMessage: "Arrival port",
          })}
          placeholder={intl.formatMessage({
            id: "general.search",
            defaultMessage: "Search",
          })}
          onInputChange={handleArrivalInputChange}
          onSelect={handleSelectPort("arrival")}
          required
        />
        {(arrivalCity || arrivalCountry) && (
          <div className="ml-3">
            <label>
              {intl.formatMessage({
                id: "tracking.components.editScheduleDialog.arrivalCity",
                defaultMessage: "Arrival City",
              })}
            </label>
            <div className="font-b4">
              {arrivalCity}
              {arrivalCity && arrivalCountry ? ", " : ""}
              {arrivalCountry}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
